import * as ExcelJS from "exceljs";
import { validateCourses } from "./courseValidation";
import { worksheetToJson } from "./excelUtils";

/**
 * Generates an Excel template for bulk course upload
 * @returns {Promise<ArrayBuffer>} ArrayBuffer containing the Excel file (for browser download)
 */
export const generateCourseExcelTemplate = async (): Promise<ArrayBuffer> => {
  // Define the column headers
  const headers = [
    "Course Name*", // Required
    "Course Type*", // Required (Core, General, Elective)
    "Description",
    "Objectives",
    "Target Audience",
    "Cost",
    "Degrees", // Comma-separated IDs
    "Number of Modules", // Default: 35
    "Number of Activities", // Default: 2
    "Course Description",
  ];

  // Create a sample row
  const sampleRow = [
    "Introduction to Computer Science",
    "Core",
    "This course provides an introduction to computer science concepts and programming fundamentals.",
    "Understand basic programming concepts, Learn problem-solving techniques, Develop simple applications",
    "Students interested in technology and programming with no prior experience",
    "99.99",
    "", // Leave empty for no degrees
    "35", // Default number of modules
    "2", // Default number of activities
    "This course introduces students to foundational concepts in computer science, including algorithms, data structures, and programming languages.",
  ];

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Course Template");

  // Add header row and sample row
  worksheet.addRow(headers);
  worksheet.addRow(sampleRow);

  // Set column widths
  worksheet.columns.forEach((col) => {
    col.width = 30;
  });

  // Generate buffer
  const buf = await workbook.xlsx.writeBuffer();

  return buf as unknown as ArrayBuffer;
};

export interface ParsedCourseResult {
  courses: any[];
  validCourses: any[];
  invalidCourses: any[];
  validationErrors: { field: string; message: string }[];
}

/**
 * Parses an Excel file for bulk course upload
 * @param file The Excel file to parse
 * @returns Object containing parsed courses and validation results
 */
export const parseCourseExcelFile = (file: File): Promise<ParsedCourseResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = e.target.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);

        // Get the first sheet
        const worksheet = workbook.worksheets[0];

        // Convert to JSON
        const jsonData = worksheetToJson(worksheet);

        // Map to course objects
        const courses = jsonData.map((row: any) => ({
          courseName: row["Course Name*"],
          description: row["Description"] || "",
          objectives: row["Objectives"] || "",
          targetAudience: row["Target Audience"] || "",
          cost: row["Cost"] ? parseFloat(row["Cost"]) : 0,
          degrees: row["Degrees"]
            ? String(row["Degrees"])
                .split(",")
                .map((id: string) => id.trim())
            : [],
          numberOfModules: row["Number of Modules"] ? parseInt(row["Number of Modules"]) : 35,
          numberOfActivities: row["Number of Activities"] ? parseInt(row["Number of Activities"]) : 2,
        }));

        // Validate courses
        const validationResult = validateCourses(courses);

        resolve({
          courses,
          validCourses: validationResult.validCourses,
          invalidCourses: validationResult.invalidCourses,
          validationErrors: validationResult.errors,
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
};
